// src/components/molecules/UserProfile/Edit/ProfileForm.js

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Alert, Snackbar, Alert as MuiAlert } from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import useStore from "../../../../store/useStore";
import {
  updateProfileInFirestore,
  updateProfileImageInAllCollections,
  createUserProfileInFirestore,
  fetchAzureUserProfile,
  fetchUserProfileFromFirestore,
  checkUserNameAvailability,
  updateAzureUserProfileUsername,
} from "../../../../services/profileService";
import { useTheme } from "styled-components";
import UserProfile from "../../../../models/UserProfile";
import ProfileImage from "./ProfilePicture/ProfileImage";
import ProfilePictureControls from "./ProfilePicture/ProfilePictureControls";
import UserNameLabel from "../../../atoms/Profile/Edit/UserNameLabel";
import DisplayNameLabel from "../../../atoms/Profile/Edit/DisplayNameLabel";
import BioLabel from "../../../atoms/Profile/Edit/BioLabel";
import UserNameInput from "./UserNameInput";
import DisplayNameInput from "./DisplayNameInput";
import BioInput from "./BioInput";
import ActionButtonsGroup from "./ActionButtons/ActionButtonsGroup";

const eventsPathRegex = /^\/challenge\/events\/[^/]+(?:\/watch)?(?:\?.*)?$/;
const validateReturnTo = (p) =>
  typeof p === "string" && eventsPathRegex.test(p) ? p : null;

// Username rules
const USERNAME_MIN_LEN = 3;
const USERNAME_MAX_LEN = 30;

// Only allow: a-z, 0-9, underscore. No spaces, periods, hyphens, etc.
const USERNAME_ALLOWED_REGEX = /^[a-z0-9_]+$/;

function normalizeUsername(inputRaw) {
  const raw = String(inputRaw || "");

  // lowercase + trim
  let v = raw.toLowerCase().trim();

  // replace whitespace runs with underscore (so spaces never persist)
  v = v.replace(/\s+/g, "_");

  // remove anything that is not a-z / 0-9 / underscore
  v = v.replace(/[^a-z0-9_]/g, "");

  // collapse multiple underscores
  v = v.replace(/_+/g, "_");

  // enforce max length
  if (v.length > USERNAME_MAX_LEN) v = v.slice(0, USERNAME_MAX_LEN);

  return v;
}

// New default rule requested:
// Use everything before "@" as the default username.
// Example: test@kbean.com -> test
// If that ends up too short (after normalization), fallback to local + first domain label.
function defaultUsernameFromEmail(emailRaw) {
  const email = String(emailRaw || "").trim().toLowerCase();
  if (!email.includes("@")) return "";

  const [localRaw, domainRaw] = email.split("@");
  const localPart = String(localRaw || "").trim();
  const domainPart = String(domainRaw || "").trim();

  const localNormalized = normalizeUsername(localPart);

  if (localNormalized.length >= USERNAME_MIN_LEN) return localNormalized;

  // fallback: local + first domain label to satisfy min length
  const firstDomainLabel = (domainPart.split(".")[0] || "").trim();
  const combined = normalizeUsername(`${localPart}${firstDomainLabel}`);

  return combined;
}

// If raw is an email (contains "@"), show a suggested username (local-part rule),
// otherwise normalize the raw value.
function coerceUsernameForField(rawUserName, emailFallback) {
  const raw = String(rawUserName || "").trim();
  if (raw.includes("@")) {
    const derived = defaultUsernameFromEmail(raw);
    if (derived) return derived;

    const fallbackDerived = defaultUsernameFromEmail(emailFallback);
    if (fallbackDerived) return fallbackDerived;

    // last resort
    return normalizeUsername(raw);
  }
  return normalizeUsername(raw);
}

function getUsernameValidation(value) {
  const v = String(value || "");

  if (!v) {
    return { valid: false, message: "Username is required." };
  }

  if (v.includes("@")) {
    return { valid: false, message: "Username cannot be an email address." };
  }

  if (v.length < USERNAME_MIN_LEN) {
    return {
      valid: false,
      message: `Username must be at least ${USERNAME_MIN_LEN} characters.`,
    };
  }

  if (v.length > USERNAME_MAX_LEN) {
    return {
      valid: false,
      message: `Username must be ${USERNAME_MAX_LEN} characters or fewer.`,
    };
  }

  if (!USERNAME_ALLOWED_REGEX.test(v)) {
    return {
      valid: false,
      message: "Use only lowercase letters, numbers, and underscore.",
    };
  }

  return { valid: true, message: "" };
}

const ProfileForm = ({ returnTo = null }) => {
  const { userProfile, setUserProfile } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const auth = getAuth();

  const [formData, setFormData] = useState({
    userName: "",
    displayName: "",
    bio: "",
    profileImage: "",
  });

  const [loadingProfile, setLoadingProfile] = useState(false);

  // existing behavior: when username is an email, force them to pick one
  const [userNameError, setUsernameError] = useState(false);

  const [userNameDisabled, setUserNameDisabled] = useState(false);

  // format/length validation
  const [userNameValidationMessage, setUserNameValidationMessage] = useState("");

  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [usernameAvailabilityMessage, setUsernameAvailabilityMessage] = useState("");
  const [checkingUsername, setCheckingUsername] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [user, setUser] = useState(null);

  // debounce timer for username checks
  const usernameTimerRef = useRef(null);

  // track latest check to avoid stale results
  const usernameCheckSeqRef = useRef(0);

  // Final resolved return target: prop -> sessionStorage -> null
  const resolvedReturnTo = useMemo(() => {
    const fromProp = validateReturnTo(returnTo);
    if (fromProp) return fromProp;

    const fromStorage = validateReturnTo(sessionStorage.getItem("returnToAfterAuth"));
    return fromStorage || null;
  }, [returnTo]);

  // Navigate helper with cleanup + fallback
  const navigateBackToCaller = () => {
    sessionStorage.removeItem("returnToAfterAuth");

    if (resolvedReturnTo) {
      navigate(resolvedReturnTo, { replace: true });
    } else {
      navigate("/challenge", { replace: true });
    }
  };

  // Listen for auth changes so we don't touch user.uid when null
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setFormData({
          userName: "",
          displayName: "",
          bio: "",
          profileImage: "",
        });
        navigate("/challenge/signin", { replace: true });
      }
    });
    return () => unsub();
  }, [auth, navigate]);

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    };
  }, []);

  const runUsernameAvailabilityCheck = (candidateUserName) => {
    if (userNameDisabled) return;

    const normalized = normalizeUsername(candidateUserName);
    const validation = getUsernameValidation(normalized);

    if (!validation.valid) {
      setUsernameAvailability(null);
      setUsernameAvailabilityMessage("");
      setCheckingUsername(false);
      return;
    }

    // IMPORTANT:
    // If the stored username is an email, we MUST check availability for the suggested username,
    // even if it matches some normalized form. So only skip checks when the stored username is NOT an email.
    const storedRaw = String(userProfile?.userName || "");
    const storedWasEmail = storedRaw.includes("@");
    const existingNormalized = normalizeUsername(storedRaw);

    if (!storedWasEmail && existingNormalized && normalized === existingNormalized) {
      setUsernameAvailability(null);
      setUsernameAvailabilityMessage("");
      setCheckingUsername(false);
      return;
    }

    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);

    setCheckingUsername(true);
    const seq = ++usernameCheckSeqRef.current;

    usernameTimerRef.current = setTimeout(async () => {
      try {
        const result = await checkUserNameAvailability(normalized);

        if (seq !== usernameCheckSeqRef.current) return;

        setUsernameAvailability(result.status);
        setUsernameAvailabilityMessage(result.message || "");
      } catch (error) {
        console.error("Error checking username availability:", error);
        if (seq !== usernameCheckSeqRef.current) return;
        setUsernameAvailability("error");
        setUsernameAvailabilityMessage(
          error?.message || "Error checking username. Please try again later."
        );
      } finally {
        if (seq === usernameCheckSeqRef.current) setCheckingUsername(false);
      }
    }, 500);
  };

  useEffect(() => {
    const maybeFetchProfile = async () => {
      if (!user || userProfile) return;

      setLoadingProfile(true);
      try {
        const firestoreProfile = await fetchUserProfileFromFirestore(user.uid);

        if (firestoreProfile) {
          const rawStored = String(firestoreProfile.userName || "");
          const storedWasEmail = rawStored.includes("@");

          // UI field can show a suggested derived username, but we DO NOT persist it on load.
          const nextUserNameForField = storedWasEmail
            ? coerceUsernameForField(rawStored, user.email)
            : (firestoreProfile.userName || "");

          setUserProfile(firestoreProfile);
          setFormData({
            userName: nextUserNameForField || "",
            displayName: firestoreProfile.displayName || "",
            bio: firestoreProfile.bio || "",
            profileImage: firestoreProfile.profileImage || "",
          });

          if (storedWasEmail) {
            setUsernameError(true);
            setUserNameDisabled(false);
            setUsernameAvailability(null);
            setUsernameAvailabilityMessage("");

            if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
          } else {
            const normalizedExisting = normalizeUsername(rawStored);
            const v = getUsernameValidation(normalizedExisting);
            if (!v.valid) {
              setUsernameError(true);
              setUserNameDisabled(false);
              setUsernameAvailability(null);
              setUsernameAvailabilityMessage("");
              if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
            } else {
              setUsernameError(false);
              setUserNameDisabled(true);
              setUsernameAvailability(null);
              setUsernameAvailabilityMessage("");
            }
          }
        } else {
          const azureProfile = await fetchAzureUserProfile(user.email);

          // IMPORTANT FIX (your #1):
          // Store EXACTLY what Azure returns (even if it's an email) in Firestore on creation.
          const azureUserNameRaw = azureProfile.userName || user.email;

          const newUserProfile = new UserProfile({
            uid: user.uid,
            displayName: azureProfile.fullName || user.displayName || "New User",
            email: azureProfile.emailAddress || user.email,
            userName: azureUserNameRaw,
            bio: azureProfile.biography || "",
            profileImage: azureProfile.profilePicture || user.photoURL || "",
          });

          await createUserProfileInFirestore(user.uid, newUserProfile.toPlainObject());

          setUserProfile(newUserProfile);

          // UI: show suggested username if the stored one is an email, but do not persist unless save.
          const storedWasEmail = String(azureUserNameRaw || "").includes("@");
          const nextUserNameForField = storedWasEmail
            ? coerceUsernameForField(azureUserNameRaw, user.email)
            : (azureUserNameRaw || "");

          setFormData({
            userName: nextUserNameForField || "",
            displayName: newUserProfile.displayName,
            bio: newUserProfile.bio,
            profileImage: newUserProfile.profileImage,
          });

          if (storedWasEmail) {
            setUsernameError(true);
            setUserNameDisabled(false);
            setUsernameAvailability(null);
            setUsernameAvailabilityMessage("");
            if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
          } else {
            const normalizedExisting = normalizeUsername(nextUserNameForField);
            const v = getUsernameValidation(normalizedExisting);
            if (!v.valid) {
              setUsernameError(true);
              setUserNameDisabled(false);
              setUsernameAvailability(null);
              setUsernameAvailabilityMessage("");
              if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
            } else {
              setUsernameError(false);
              setUserNameDisabled(true);
              setUsernameAvailability(null);
              setUsernameAvailabilityMessage("");
            }
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoadingProfile(false);
      }
    };

    if (userProfile) {
      const rawStored = String(userProfile.userName || "");
      const storedWasEmail = rawStored.includes("@");

      const nextUserNameForField = storedWasEmail
        ? coerceUsernameForField(rawStored, user?.email)
        : (userProfile.userName || "");

      setFormData({
        userName: nextUserNameForField || "",
        displayName: userProfile.displayName || "",
        bio: userProfile.bio || "",
        profileImage: userProfile.profileImage || "",
      });

      if (storedWasEmail) {
        setUsernameError(true);
        setUserNameDisabled(false);
        setUsernameAvailability(null);
        setUsernameAvailabilityMessage("");
        if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
      } else {
        const normalizedExisting = normalizeUsername(rawStored);
        const v = getUsernameValidation(normalizedExisting);
        if (!v.valid) {
          setUsernameError(true);
          setUserNameDisabled(false);
          setUsernameAvailability(null);
          setUsernameAvailabilityMessage("");
          if (nextUserNameForField) runUsernameAvailabilityCheck(nextUserNameForField);
        } else {
          setUsernameError(false);
          setUserNameDisabled(true);
          setUsernameAvailability(null);
          setUsernameAvailabilityMessage("");
        }
      }
    } else {
      maybeFetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userProfile, setUserProfile]);

  // validate on username change
  useEffect(() => {
    const normalized = normalizeUsername(formData.userName);
    const v = getUsernameValidation(normalized);

    if (!userNameDisabled) {
      setUserNameValidationMessage(v.message);

      if (formData.userName !== normalized) {
        setFormData((prev) => ({ ...prev, userName: normalized }));
      }
    } else {
      setUserNameValidationMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.userName, userNameDisabled]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "userName") {
      const normalized = normalizeUsername(value);

      setFormData((prev) => ({ ...prev, userName: normalized }));

      setUsernameAvailability(null);
      setUsernameAvailabilityMessage("");

      runUsernameAvailabilityCheck(normalized);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async () => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) {
      setSnackbarMessage("You must be signed in to update your profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const normalizedUserName = normalizeUsername(formData.userName);
    const validation = getUsernameValidation(normalizedUserName);

    const storedRaw = String(userProfile.userName || "");
    const storedWasEmail = storedRaw.includes("@");

    // Compare against the stored raw username (normalized), but treat "storedWasEmail" as requiring correction
    const existingNormalized = normalizeUsername(storedRaw);
    const isUserNameChanged = normalizedUserName !== existingNormalized;

    // If username is editable OR the stored username was an email, enforce validation & availability checks.
    const mustValidateAndCheckAvailability = !userNameDisabled || storedWasEmail;

    if (mustValidateAndCheckAvailability) {
      if (!validation.valid) {
        setSnackbarMessage(validation.message || "Please enter a valid username.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (usernameAvailability === "taken") {
        setSnackbarMessage("That username is already taken.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // If we are correcting an email username, require availability even if normalization looks unchanged.
      const mustRequireAvailable = isUserNameChanged || storedWasEmail;
      if (mustRequireAvailable && usernameAvailability !== "available") {
        const msg =
          usernameAvailability === "error"
            ? usernameAvailabilityMessage || "Error checking username. Please try again later."
            : "Please confirm username availability before saving.";

        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    }

    try {
      const nextFormData = {
        ...formData,
        userName: normalizedUserName,
      };

      const emailForAzure = current.email || userProfile.email || user?.email || null;
      if (!emailForAzure) {
        setSnackbarMessage("We could not confirm your email address. Please re-login and try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Call Azure update when needed:
      // - user changed username, OR
      // - stored username is an email (must correct it)
      // This fixes the original bug without spamming Azure on every save.
      if (isUserNameChanged || storedWasEmail) {
        await updateAzureUserProfileUsername(normalizedUserName, emailForAzure);
        const token = await user.getIdToken();

        const socialAuthenticateURL = process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
        const authData = new FormData();
        authData.append("fbtoken", token);

        const authResponseData = await postFormData(
          socialAuthenticateURL,
          authData
        );
        localStorage.setItem("authResponseData", authResponseData);
      }

      // Only after Azure succeeds do we write username to Firestore.
      const updatedProfile = {
        ...userProfile,
        ...nextFormData,
        uid: current.uid,
      };

      await updateProfileInFirestore(current.uid, updatedProfile);
      setUserProfile(updatedProfile);
      setFormData((prev) => ({ ...prev, userName: updatedProfile.userName || "" }));

      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      if (!(updatedProfile.userName || "").includes("@")) {
        setUserNameDisabled(true);
        setUsernameError(false);
      }

      navigateBackToCaller();
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbarMessage(error?.message || "Error updating profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const postFormData = async (url, data) => {
    const response = await fetch(url, { method: "POST", body: data });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.text();
  };

  const handleImageUpload = async (imageUrl) => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) return;

    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));

    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: current.uid,
    };
    await updateProfileInFirestore(current.uid, updatedProfile);
    setUserProfile(updatedProfile);

    await updateProfileImageInAllCollections(current.uid, imageUrl);
  };

  const handleRemoveProfilePicture = async () => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) return;

    const updatedProfile = { ...userProfile, profileImage: "", uid: current.uid };
    setFormData((prev) => ({ ...prev, profileImage: "" }));
    await updateProfileInFirestore(current.uid, updatedProfile);
    setUserProfile(updatedProfile);
    await updateProfileImageInAllCollections(current.uid, "");
  };

  const handleCancelEdit = () => {
    navigateBackToCaller();
  };

  if (loadingProfile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  if (!user) return null;
  if (!userProfile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  const showFormatError = !userNameDisabled && !!userNameValidationMessage;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "basic-sans",
          fontWeight: 700,
          fontSize: "2em",
          mb: 10,
        }}
      >
        Edit My Profile & Settings
      </Typography>

      {/* Profile Picture Section */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Profile Picture
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38", mb: 8 }}
      >
        <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
          <ProfileImage src={formData.profileImage} />
        </Grid>
        <Grid item xs={12} sm={9}>
          <ProfilePictureControls
            handleRemoveProfilePicture={handleRemoveProfilePicture}
            theme={theme}
            onImageUpload={handleImageUpload}
            userId={userProfile.uid}
          />
        </Grid>
      </Grid>

      {/* Name and Bio */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Name and Bio
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38" }}
      >
        <UserNameLabel />
        <Grid item xs={12} sm={9}>
          {userNameError && (
            <Alert variant="filled" severity="error" sx={{ mb: 4 }}>
              Please create a unique User Name (not an email address).
            </Alert>
          )}

          {showFormatError && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {userNameValidationMessage} (Allowed: a-z, 0-9, underscore. {USERNAME_MIN_LEN}–{USERNAME_MAX_LEN} chars.)
            </Alert>
          )}

          {checkingUsername && (
            <Alert variant="filled" severity="info" sx={{ mb: 2 }}>
              Checking username availability...
            </Alert>
          )}
          {usernameAvailability === "available" && (
            <Alert
              variant="filled"
              severity="success"
              sx={{ mb: 2, backgroundColor: theme.colors.green }}
            >
              Username is available!
            </Alert>
          )}
          {usernameAvailability === "taken" && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              Username is already taken.
            </Alert>
          )}
          {usernameAvailability === "error" && (
            <Alert variant="filled" severity="warning" sx={{ mb: 2 }}>
              {usernameAvailabilityMessage || "Error checking username. Please try again later."}
            </Alert>
          )}

          <UserNameInput
            theme={theme}
            userName={formData.userName}
            disabled={userNameDisabled}
            handleChange={handleChange}
            maxLength={USERNAME_MAX_LEN}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38" }}
      >
        <DisplayNameLabel />
        <Grid item xs={12} sm={9}>
          <DisplayNameInput displayName={formData.displayName} handleChange={handleChange} />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38", mb: 8 }}
      >
        <BioLabel />
        <Grid item xs={12} sm={9}>
          <BioInput bio={formData.bio} handleChange={handleChange} />
        </Grid>
      </Grid>

      <ActionButtonsGroup handleCancel={handleCancelEdit} handleSave={handleUpdateProfile} />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <MuiAlert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ width: "100%", backgroundColor: theme.colors.green }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default ProfileForm;
